import React, { useState, useMemo } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from './ui/dialog';
import { InfoTip } from './InfoTip';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Badge } from './ui/badge';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from './ui/table';
import { Search, ArrowRight, AlertTriangle, CheckCircle, Trash2, DollarSign } from 'lucide-react';

const formatCurrency = (v) => {
  const n = Number(v) || 0;
  return `R ${n < 0 ? '-' : ''}${Math.abs(n).toLocaleString('en-ZA', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
};

const formatDate = (d) => {
  if (!d) return '-';
  try { return new Date(d).toLocaleDateString('en-ZA', { year: 'numeric', month: 'short', day: 'numeric' }); }
  catch { return d; }
};

export function AllocationDialog({ open, onOpenChange, bankTxn, unmatchedRecorded, onConfirm, loading, filterNote }) {
  const [search, setSearch] = useState('');
  const [allocations, setAllocations] = useState({});
  const [dateOverrides, setDateOverrides] = useState({});
  const [writeOffAmount, setWriteOffAmount] = useState('');
  const [writeOffReason, setWriteOffReason] = useState('');

  // Reset state whenever the bank txn changes (new allocation session)
  React.useEffect(() => {
    setAllocations({});
    setDateOverrides({});
    setWriteOffAmount('');
    setWriteOffReason('');
    setSearch('');
  }, [bankTxn?.id]);

  // Bank amount is SIGNED — positive for deposits, negative for reversals/refunds.
  // Allocations made from a negative bank must themselves be negative.
  const originalBankAmount = Number(bankTxn?.amount) || 0;
  const bankSign = originalBankAmount < 0 ? -1 : 1;
  const bankAmount = bankTxn?.remaining_balance != null
    ? Number(bankTxn.remaining_balance) * bankSign  // remaining_balance is abs from backend; re-sign it
    : originalBankAmount;
  const alreadyAllocated = Number(bankTxn?.allocated_amount) || Number(bankTxn?.already_allocated) || 0;
  const isPartiallyAllocated = Math.abs(alreadyAllocated) > 0;
  const bankDate = (bankTxn?.posting_date || bankTxn?.value_date || '').slice(0, 10);

  // Filter unmatched recorded transactions
  const filtered = useMemo(() => {
    if (!unmatchedRecorded) return [];
    const q = search.toLowerCase();
    return unmatchedRecorded.filter(t => {
      if (!q) return true;
      return (
        (t.investor_name || '').toLowerCase().includes(q) ||
        (t.reference || '').toLowerCase().includes(q) ||
        (t.loan_id || '').toLowerCase().includes(q) ||
        (t.notes || '').toLowerCase().includes(q) ||
        String(t.amount || '').includes(q)
      );
    });
  }, [unmatchedRecorded, search]);

  const totalAllocated = Object.values(allocations).reduce((s, v) => s + (parseFloat(v) || 0), 0);
  const writeOff = parseFloat(writeOffAmount) || 0;
  // Capacity math — we compare the SIGNED net of allocations to the SIGNED bank.
  // This lets multi-sign splits (e.g. two repayments + one negative correction
  // that net to the bank amount) validate correctly. Magnitudes are used only
  // for the progress bar.
  const absBank = Math.abs(bankAmount);
  const netUsedMag = Math.abs(totalAllocated) + writeOff;
  const remaining = absBank - netUsedMag;
  const pctAllocated = absBank > 0 ? Math.min((netUsedMag / absBank) * 100, 100) : 0;

  const setAmount = (id, val) => {
    setAllocations(prev => {
      const next = { ...prev };
      if (val === '' || val === '0') { delete next[id]; }
      else { next[id] = val; }
      return next;
    });
  };

  const autoFillAmount = (txn) => {
    // For partially-allocated recorded txns, only fill the remaining portion.
    // The capacity check uses the SIGNED net of existing allocations (not
    // sum-of-absolutes) so multi-sign splits are handled correctly. We then
    // fill `txn` with the sign that brings the net closer to the bank.
    const txnAmt = Math.abs(
      Number(txn.remaining_to_allocate ?? txn.amount) || 0
    );
    const currentlyAllocatedSigned = Object.entries(allocations)
      .filter(([id]) => id !== txn.id)
      .reduce((s, [, v]) => s + (parseFloat(v) || 0), 0);
    // Remaining capacity in the bank's SIGNED frame = bankAmount − currentSigned − (writeOff * bankSign)
    const remainingSigned = bankAmount - currentlyAllocatedSigned - (writeOff * bankSign);
    const fillSigned = Math.sign(remainingSigned) * Math.min(txnAmt, Math.abs(remainingSigned));
    if (Math.abs(fillSigned) > 0) {
      setAmount(txn.id, fillSigned.toFixed(2));
      if (!dateOverrides[txn.id] && bankDate) {
        setDateOverrides(prev => ({ ...prev, [txn.id]: bankDate }));
      }
    }
  };

  const removeAllocation = (id) => {
    setAllocations(prev => { const n = { ...prev }; delete n[id]; return n; });
    setDateOverrides(prev => { const n = { ...prev }; delete n[id]; return n; });
  };

  const handleWriteOffRemaining = () => {
    if (remaining > 0) {
      setWriteOffAmount(remaining.toFixed(2));
      setWriteOffReason('Rounding / small balance write-off');
    }
  };

  const handleConfirm = () => {
    // Accept BOTH positive and negative allocations — negative ones come from
    // reversing/refund bank transactions (e.g. -R2.6M outflow split across
    // multiple uploaded deal_purchases). Only filter out zero/empty entries.
    const allocationList = Object.entries(allocations)
      .filter(([, v]) => Math.abs(parseFloat(v) || 0) > 0)
      .map(([id, amount]) => ({
        transaction_id: id,
        amount: parseFloat(amount),
        effective_date: dateOverrides[id] || null,
      }));

    onConfirm({
      bank_transaction_id: bankTxn.id,
      bank_amount: originalBankAmount,
      allocations: allocationList,
      write_off_amount: writeOff,
      write_off_reason: writeOffReason || null,
    });
  };

  const handleReset = () => {
    setAllocations({});
    setDateOverrides({});
    setWriteOffAmount('');
    setWriteOffReason('');
  };

  const allocatedTxnIds = Object.keys(allocations).filter(id => Math.abs(parseFloat(allocations[id]) || 0) > 0);
  const hasAllocations = allocatedTxnIds.length > 0 || writeOff > 0;
  const isOverAllocated = remaining < -1;

  if (!bankTxn) return null;

  // Mobile: full-screen sheet, ONE scroll area between a compact header and a
  // pinned action bar, recorded rows as cards. Desktop (sm+): unchanged
  // centred dialog with the table.
  return (
    <Dialog open={open} onOpenChange={(o) => { if (!o) handleReset(); onOpenChange(o); }}>
      <DialogContent
        className="flex flex-col gap-2 p-3 w-screen max-w-none h-[100dvh] max-h-[100dvh] rounded-none sm:gap-4 sm:p-6 sm:w-full sm:max-w-5xl sm:h-auto sm:max-h-[90vh] sm:rounded-lg recon-big-checks"
        data-testid="allocation-dialog"
      >
        <DialogHeader className="text-left pr-8">
          <DialogTitle className="flex items-center gap-2 text-base sm:text-lg">
            <DollarSign className="h-5 w-5 text-amber-600 flex-shrink-0" />
            Allocate Bank Transaction
            <InfoTip testId="allocation-info">
              Split this bank transaction across one or more recorded transactions. Tap an amount field to
              auto-fill it from the bank's remaining balance; the effective date pre-fills with the bank's
              posting date. Any small leftover can be written off once something is allocated.
            </InfoTip>
          </DialogTitle>
          <DialogDescription className="sr-only">
            Split this bank transaction across one or more recorded transactions.
          </DialogDescription>
        </DialogHeader>
        {filterNote && (
          <div className="text-xs rounded border border-blue-200 bg-blue-50 text-blue-800 px-2 py-1" data-testid="allocation-filter-note">
            {filterNote}. Clear the filter in Reconciliation to see every row.
          </div>
        )}

        <div className="flex-1 min-h-0 overflow-y-auto flex flex-col gap-2 sm:gap-3 -mx-1 px-1">
          {/* Bank Transaction Summary */}
          <div className="bg-slate-50 border rounded-lg p-3 sm:p-4 space-y-2 sm:space-y-3" data-testid="allocation-bank-summary">
            <div className="flex flex-col gap-1 sm:flex-row sm:items-center sm:justify-between">
              <div className="min-w-0">
                <p className="text-[11px] text-slate-500 uppercase tracking-wide">Bank Transaction</p>
                <p className="font-medium text-sm mt-0.5 break-words">{bankTxn.description || 'No description'}</p>
                <p className="text-xs text-slate-500 mt-0.5">{formatDate(bankTxn.posting_date || bankTxn.value_date)}</p>
              </div>
              <div className="sm:text-right">
                <p className="text-xl sm:text-2xl font-bold text-slate-800" data-testid="allocation-bank-amount">
                  {isPartiallyAllocated ? formatCurrency(bankAmount) : formatCurrency(originalBankAmount)}
                </p>
                {isPartiallyAllocated && (
                  <div className="text-xs text-slate-500 mt-0.5 flex flex-wrap gap-x-2">
                    <span>Original: {formatCurrency(originalBankAmount)}</span>
                    <span className="text-amber-600">Already allocated: {formatCurrency(alreadyAllocated)}</span>
                  </div>
                )}
              </div>
            </div>

            {/* Running Balance Bar */}
            <div className="space-y-1">
              <div className="flex flex-wrap justify-between gap-x-3 text-xs">
                <span className="text-slate-500">Allocated: <strong className="text-green-700">{formatCurrency(totalAllocated)}</strong></span>
                {writeOff > 0 && <span className="text-amber-600">Write-off: <strong>{formatCurrency(writeOff)}</strong></span>}
                <span className={`font-semibold ${remaining < 0 ? 'text-red-600' : remaining < 1 ? 'text-green-600' : 'text-slate-600'}`}>
                  Remaining: {formatCurrency((remaining > 0 ? remaining : 0) * bankSign)}
                </span>
              </div>
              <div className="w-full bg-slate-200 rounded-full h-2 sm:h-3 overflow-hidden">
                <div
                  className={`h-full transition-all duration-300 rounded-full ${isOverAllocated ? 'bg-red-500' : pctAllocated >= 99.9 ? 'bg-green-500' : 'bg-amber-500'}`}
                  style={{ width: `${Math.min(pctAllocated + (writeOff / bankAmount * 100), 100)}%` }}
                  data-testid="allocation-progress-bar"
                />
              </div>
            </div>
          </div>

          {/* Allocated Summary (if any) */}
          {allocatedTxnIds.length > 0 && (
            <div className="border rounded-lg p-2 sm:p-3 bg-green-50 border-green-200" data-testid="allocation-summary">
              <p className="text-xs font-semibold text-green-800 mb-1.5">Allocations ({allocatedTxnIds.length})</p>
              <div className="space-y-1 max-h-32 overflow-y-auto">
                {allocatedTxnIds.map(id => {
                  const txn = unmatchedRecorded?.find(t => t.id === id);
                  return (
                    <div key={id} className="flex items-center justify-between gap-2 text-xs bg-white p-1.5 rounded border">
                      <div className="flex-1 min-w-0">
                        <div className="font-medium truncate">{txn?.investor_name || txn?.reference || id}</div>
                        {dateOverrides[id] && <div className="text-amber-600">date: {dateOverrides[id]}</div>}
                      </div>
                      <div className="flex items-center gap-2 flex-shrink-0">
                        <span className="font-mono font-bold text-green-700">{formatCurrency(allocations[id])}</span>
                        <button onClick={() => removeAllocation(id)} className="p-1 text-red-400 hover:text-red-600" aria-label="Remove allocation"><Trash2 className="h-3.5 w-3.5" /></button>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          {/* Search */}
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
            <Input
              placeholder="Search investor, reference, loan ID…"
              value={search}
              onChange={e => setSearch(e.target.value)}
              className="pl-9"
              data-testid="allocation-search"
            />
          </div>

          {/* Mobile: recorded rows as cards */}
          <div className="sm:hidden space-y-2" data-testid="allocation-txn-cards">
            {filtered.length === 0 ? (
              <p className="text-center text-sm text-slate-400 py-6">No unmatched transactions found</p>
            ) : filtered.map((txn, i) => {
              const isAllocated = !!allocations[txn.id] && parseFloat(allocations[txn.id]) > 0;
              const txnRemaining = Number(txn.remaining_to_allocate);
              const isPartial = !Number.isNaN(txnRemaining) && Number(txn.allocated_amount) > 0;
              return (
                <div
                  key={txn.id}
                  className={`border rounded-lg p-2.5 ${isAllocated ? 'bg-green-50 border-green-300' : isPartial ? 'bg-indigo-50' : 'bg-white'}`}
                  data-testid={`allocation-card-${i}`}
                >
                  <div className="flex items-start justify-between gap-2">
                    <div className="min-w-0">
                      <div className="text-sm font-medium break-words">{txn.investor_name || '-'}</div>
                      <div className="text-xs text-slate-600 break-words">{txn.reference || txn.notes || '-'}</div>
                      <div className="text-xs text-slate-500">{formatDate(txn.transaction_date)}</div>
                    </div>
                    <div className="text-right flex-shrink-0">
                      <div className="font-mono text-sm font-semibold">{formatCurrency(txn.amount)}</div>
                      {isPartial && (
                        <div className="text-[11px]">
                          <div className="text-indigo-700">Alloc: {formatCurrency(txn.allocated_amount)}</div>
                          <div className="text-red-600">Rem: {formatCurrency(txnRemaining)}</div>
                        </div>
                      )}
                    </div>
                  </div>
                  <div className="mt-2 grid grid-cols-[1fr_auto] gap-2 items-end">
                    <div className="grid grid-cols-2 gap-2">
                      <label className="text-[11px] text-slate-500">
                        Allocate
                        <Input
                          type="number"
                          inputMode="decimal"
                          step="0.01"
                          placeholder="0.00"
                          value={allocations[txn.id] || ''}
                          onChange={e => setAmount(txn.id, e.target.value)}
                          onFocus={() => { if (!allocations[txn.id]) autoFillAmount(txn); }}
                          className="h-9 mt-0.5 text-right text-sm font-mono"
                          data-testid={`allocation-amount-m-${i}`}
                        />
                      </label>
                      <label className="text-[11px] text-slate-500">
                        Effective date
                        <Input
                          type="date"
                          value={dateOverrides[txn.id] || ''}
                          onChange={e => setDateOverrides(prev => ({ ...prev, [txn.id]: e.target.value }))}
                          className="h-9 mt-0.5 text-xs"
                          data-testid={`allocation-date-m-${i}`}
                        />
                      </label>
                    </div>
                    {isAllocated ? (
                      <Button type="button" variant="outline" size="sm" className="h-9 px-2 text-red-600" onClick={() => removeAllocation(txn.id)} aria-label="Remove allocation">
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    ) : (
                      <Button type="button" variant="outline" size="sm" className="h-9 px-2 text-green-700" onClick={() => autoFillAmount(txn)}>
                        Fill
                      </Button>
                    )}
                  </div>
                </div>
              );
            })}
          </div>

          {/* Desktop: Unmatched Recorded Transactions Table */}
          <div className="hidden sm:block sm:min-h-[12rem] sm:max-h-[45vh] overflow-y-auto border rounded-lg" data-testid="allocation-txn-table">
            <Table>
              <TableHeader className="sticky top-0 bg-white z-10">
                <TableRow>
                  <TableHead className="w-36">Investor</TableHead>
                  <TableHead>Reference</TableHead>
                  <TableHead>Date</TableHead>
                  <TableHead className="text-right">Amount</TableHead>
                  <TableHead className="w-36 text-right">Allocate</TableHead>
                  <TableHead className="w-32">Date Override</TableHead>
                  <TableHead className="w-12"></TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {filtered.length === 0 ? (
                  <TableRow><TableCell colSpan={7} className="text-center text-slate-400 py-8">No unmatched transactions found</TableCell></TableRow>
                ) : (
                  filtered.map((txn, i) => {
                    const isAllocated = !!allocations[txn.id] && parseFloat(allocations[txn.id]) > 0;
                    const txnRemaining = Number(txn.remaining_to_allocate);
                    const isPartial = !Number.isNaN(txnRemaining) && Number(txn.allocated_amount) > 0;
                    return (
                      <TableRow
                        key={txn.id}
                        className={`${isAllocated ? 'bg-green-50' : isPartial ? 'bg-indigo-50' : 'hover:bg-slate-50'} transition-colors`}
                        data-testid={`allocation-row-${i}`}
                      >
                        <TableCell className="text-sm font-medium truncate max-w-[140px]" title={txn.investor_name}>
                          {txn.investor_name || '-'}
                        </TableCell>
                        <TableCell className="text-sm text-slate-600 truncate max-w-[180px]" title={txn.reference || txn.notes}>
                          {txn.reference || txn.notes || '-'}
                        </TableCell>
                        <TableCell className="text-sm">{formatDate(txn.transaction_date)}</TableCell>
                        <TableCell className="text-right font-mono text-sm">
                          {formatCurrency(txn.amount)}
                          {isPartial && (
                            <div className="text-[10px] font-normal mt-0.5">
                              <span className="text-indigo-700">Alloc: {formatCurrency(txn.allocated_amount)}</span>
                              <span className="text-red-600 ml-1">Rem: {formatCurrency(txnRemaining)}</span>
                            </div>
                          )}
                        </TableCell>
                        <TableCell className="text-right">
                          <Input
                            type="number"
                            step="0.01"
                            placeholder="0.00"
                            value={allocations[txn.id] || ''}
                            onChange={e => setAmount(txn.id, e.target.value)}
                            onFocus={() => { if (!allocations[txn.id]) autoFillAmount(txn); }}
                            className="h-7 text-right text-sm font-mono w-full"
                            data-testid={`allocation-amount-${i}`}
                          />
                        </TableCell>
                        <TableCell>
                          <Input
                            type="date"
                            value={dateOverrides[txn.id] || ''}
                            onChange={e => setDateOverrides(prev => ({ ...prev, [txn.id]: e.target.value }))}
                            className="h-7 text-xs"
                            data-testid={`allocation-date-${i}`}
                          />
                        </TableCell>
                        <TableCell>
                          {isAllocated ? (
                            <button onClick={() => removeAllocation(txn.id)} className="text-red-400 hover:text-red-600">
                              <Trash2 className="h-3.5 w-3.5" />
                            </button>
                          ) : (
                            <button onClick={() => autoFillAmount(txn)} className="text-green-400 hover:text-green-600" title="Auto-fill amount">
                              <ArrowRight className="h-3.5 w-3.5" />
                            </button>
                          )}
                        </TableCell>
                      </TableRow>
                    );
                  })
                )}
              </TableBody>
            </Table>
          </div>

          {/* Write-off Section — show whenever there are allocations and capacity left */}
          {remaining > 0.01 && Math.abs(totalAllocated) > 0 && (
            <div className="border rounded-lg p-2.5 sm:p-3 bg-amber-50 border-amber-200" data-testid="allocation-writeoff-section">
              <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between mb-2">
                <div className="flex items-center gap-2">
                  <AlertTriangle className="h-4 w-4 text-amber-600 flex-shrink-0" />
                  <span className="text-sm font-medium text-amber-800">Remaining: {formatCurrency(remaining)}</span>
                </div>
                <Button variant="outline" size="sm" onClick={handleWriteOffRemaining} className="text-xs" data-testid="allocation-writeoff-btn">
                  Write Off Remaining
                </Button>
              </div>
              <div className="flex flex-col gap-2 sm:flex-row">
                <Input
                  type="number"
                  inputMode="decimal"
                  step="0.01"
                  min="0"
                  placeholder="Write-off amount"
                  value={writeOffAmount}
                  onChange={e => setWriteOffAmount(e.target.value)}
                  className="h-9 sm:h-8 sm:w-32 text-sm"
                  data-testid="allocation-writeoff-amount"
                />
                <Input
                  placeholder="Reason (e.g., rounding difference)"
                  value={writeOffReason}
                  onChange={e => setWriteOffReason(e.target.value)}
                  className="h-9 sm:h-8 flex-1 text-sm"
                  data-testid="allocation-writeoff-reason"
                />
              </div>
            </div>
          )}
        </div>

        {/* Pinned action bar */}
        <div className="flex flex-col gap-2 border-t pt-2 sm:pt-4 sm:flex-row sm:items-center sm:justify-between">
          <div className="flex items-center gap-2 text-xs min-h-[1.25rem]">
            {isOverAllocated && (
              <Badge variant="destructive" className="text-xs">Over-allocated by {formatCurrency(Math.abs(remaining))}</Badge>
            )}
            {!isOverAllocated && remaining < 1 && hasAllocations && (
              <Badge className="bg-green-100 text-green-800 text-xs"><CheckCircle className="h-3 w-3 mr-1" />Fully Allocated</Badge>
            )}
          </div>
          <div className="grid grid-cols-2 gap-2 sm:flex">
            <Button
              onClick={handleConfirm}
              disabled={!hasAllocations || isOverAllocated || loading}
              className="col-span-2 order-first sm:order-last bg-green-600 hover:bg-green-700 text-white"
              data-testid="allocation-confirm-btn"
            >
              {loading ? 'Saving...' : `Confirm ${allocatedTxnIds.length} Allocation(s)`}
            </Button>
            <Button variant="outline" onClick={() => { handleReset(); onOpenChange(false); }} data-testid="allocation-cancel-btn">Cancel</Button>
            <Button variant="outline" onClick={handleReset} data-testid="allocation-reset-btn">Reset</Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
